import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { dictionaries, type Locale, type StringKey } from './strings';

export type Direction = 'rtl' | 'ltr';

export interface I18nValue {
  locale: Locale;
  dir: Direction;
  setLocale: (next: Locale) => void;
  toggleLocale: () => void;
  /** Translates a key, interpolating `{name}` placeholders. */
  t: (key: StringKey, params?: Record<string, string | number>) => string;
  /** Picks the Arabic or English member of a bilingual record from the API. */
  bilingual: (ar: string | null | undefined, en: string | null | undefined) => string;
  formatDateTime: (iso: string | null | undefined) => string;
  formatNumber: (value: number) => string;
}

const STORAGE_KEY = 'moqeem.locale';

const I18nContext = createContext<I18nValue | null>(null);

/** Arabic-first: the default locale is `ar` and the default direction is `rtl`. */
export function readInitialLocale(): Locale {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'ar' || stored === 'en') return stored;
  } catch {
    /* a private window or blocked storage simply gets the default */
  }
  return 'ar';
}

export function directionOf(locale: Locale): Direction {
  return locale === 'ar' ? 'rtl' : 'ltr';
}

export function I18nProvider({ children, initial }: { children: ReactNode; initial?: Locale }): ReactElement {
  const [locale, setLocaleState] = useState<Locale>(() => initial ?? readInitialLocale());
  const dir = directionOf(locale);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('lang', locale);
    root.setAttribute('dir', dir);
  }, [locale, dir]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* the choice still applies to this page */
    }
  }, []);

  const value = useMemo<I18nValue>(() => {
    const dict = dictionaries[locale];
    const t = (key: StringKey, params?: Record<string, string | number>): string => {
      const raw = dict[key];
      if (!params) return raw;
      return raw.replace(/\{(\w+)\}/g, (match, name: string) =>
        Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : match,
      );
    };
    const intlLocale = locale === 'ar' ? 'ar-SA' : 'en-GB';
    return {
      locale,
      dir,
      setLocale,
      toggleLocale: () => setLocale(locale === 'ar' ? 'en' : 'ar'),
      t,
      bilingual: (arText, enText) => (locale === 'ar' ? (arText ?? enText ?? '') : (enText ?? arText ?? '')),
      formatDateTime: (iso) => {
        if (!iso) return '—';
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return iso;
        return new Intl.DateTimeFormat(intlLocale, {
          dateStyle: 'medium',
          timeStyle: 'short',
        }).format(d);
      },
      formatNumber: (n) => new Intl.NumberFormat(intlLocale).format(n),
    };
  }, [locale, dir, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
}
